import { ENEMIES } from '../../data/enemies';
import { GROUND_Y, SIM_HZ } from '../../data/tuning';

/**
 * EN-04 Sicario en parapente (GDD §8.2, issue #86): descends at 1 u/s with a sine drift (no randomness) and so
 * blocks a gap for a while; with a motor (Veneno, #98) it flies level to the left instead. At the ground it lands and walks off with the scroll: it stops being dangerous and
 * nobody falls or dies (GDD §3.4). Pure and deterministic; the sail does no damage, only the rider's box does.
 */
export const EN04 = ENEMIES['EN-04'];

export interface ParagliderState {
  /** Ticks since it was first active (it keeps flying after it leaves the screen). */
  ticks: number;
  landed: boolean;
  /** Where it was when it landed: it stays there while the screen scrolls away. */
  x: number;
}

/** Per-instance overrides from the level data (`SegmentDef.enemies[].params`), for the Veneno event (#98). */
export interface ParagliderParams {
  driftAmplitude?: number;
  driftHz?: number;
  /** 1: Veneno's motor paraglider, flying level to the left at EN04.motorSpeed; it never lands. */
  motor?: number;
}

export const hasMotor = (params?: ParagliderParams): boolean => params?.motor === 1;

export const createParaglider = (x: number): ParagliderState => ({ ticks: 0, landed: false, x });

/** The lowest centre of the rider: his feet on the ground. */
export const LANDING_Y = GROUND_Y + EN04.hitboxHeight / 2;

/** One tick: returns where the paraglider is now. */
export function stepParaglider(
  state: ParagliderState,
  spawn: { x: number; y: number },
  params: ParagliderParams = {},
): { x: number; y: number } {
  if (state.landed) return { x: state.x, y: LANDING_Y };
  state.ticks += 1;
  if (hasMotor(params)) return { x: spawn.x - (EN04.motorSpeed * state.ticks) / SIM_HZ, y: spawn.y };
  const amplitude = params.driftAmplitude ?? EN04.driftAmplitude;
  const hz = params.driftHz ?? EN04.driftHz;
  const x = spawn.x + amplitude * Math.sin((2 * Math.PI * hz * state.ticks) / SIM_HZ);
  const y = spawn.y - (EN04.descentSpeed * state.ticks) / SIM_HZ;
  if (y <= LANDING_Y) {
    state.landed = true;
    state.x = x;
    return { x, y: LANDING_Y };
  }
  return { x, y };
}
